import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Target, CheckCircle2, Circle, ArrowRight, GraduationCap, ShieldCheck, Clock,
  Gauge, Layers, TrendingUp, X, Sparkles, Flame, BookOpen, Award, Network,
} from "lucide-react";
import { MentisMark } from "@/components/MentisMark";

/* ----------------------------------------------------------------- helpers */
export function CountUp({ value, duration = 750, suffix = "" }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (value == null) return;
    let raf;
    const start = performance.now();
    const from = 0;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(from + (value - from) * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);
  return <>{n}{suffix}</>;
}

const scoreColor = (s) => (s == null ? "#64748B" : s >= 70 ? "#10B981" : s >= 55 ? "#F59E0B" : "#EF4444");

const STATUS = {
  verified: { label: "Verified", color: "#10B981", bg: "rgba(16,185,129,0.12)" },
  inferred: { label: "Inferred", color: "#F59E0B", bg: "rgba(245,158,11,0.12)" },
  not_assessed: { label: "Not Assessed", color: "#64748B", bg: "rgba(100,116,139,0.14)" },
};

export const StatusBadge = ({ status, testid }) => {
  const s = STATUS[status] || STATUS.not_assessed;
  return (
    <span
      data-testid={testid}
      className="mono-label inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[9px]"
      style={{ color: s.color, background: s.bg, letterSpacing: "0.14em" }}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.color }} />
      {s.label}
    </span>
  );
};

const ScoreBar = ({ score, target }) => (
  <div className="flex items-center gap-3">
    <div className="relative flex-1 h-2 rounded-full bg-black/[0.05] overflow-hidden">
      {target != null && (
        <div className="absolute top-0 bottom-0 w-px bg-white/30" style={{ left: `${target}%` }} />
      )}
      <motion.div
        className="h-full rounded-full"
        style={{ background: scoreColor(score) }}
        initial={{ width: 0 }}
        animate={{ width: `${score ?? 0}%` }}
        transition={{ duration: 0.8, ease: "easeOut" }}
      />
    </div>
    <div className="w-9 text-right font-mono text-sm font-semibold" style={{ color: scoreColor(score) }}>
      {score == null ? "?" : <CountUp value={score} />}
    </div>
  </div>
);

const CardShell = ({ children, title, eyebrow, icon, testid, delay = 0 }) => (
  <motion.section
    data-testid={testid}
    initial={{ opacity: 0, y: 18 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay, ease: "easeOut" }}
    className="surface rounded-2xl p-6 sm:p-7"
  >
    {(title || eyebrow) && (
      <div className="mb-5">
        {eyebrow && <div className="mono-label text-orange-600 mb-2 flex items-center gap-2">{icon}{eyebrow}</div>}
        {title && <h2 className="font-display text-xl sm:text-2xl font-bold tracking-tight text-slate-900">{title}</h2>}
      </div>
    )}
    {children}
  </motion.section>
);

/* -------------------------------------------------------------- Goal card */
export const GoalCard = ({ goal, learner }) => (
  <CardShell testid="goal-card" eyebrow="Your Goal" icon={<Target size={13} />}>
    <div className="flex items-start justify-between gap-4">
      <div>
        <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 leading-tight">
          {goal?.title}
        </h2>
        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-slate-700">
          {learner?.years_experience && <span className="px-2.5 py-1 rounded-full bg-black/[0.04]">{learner.years_experience} yrs experience</span>}
          {learner?.industry && <span className="px-2.5 py-1 rounded-full bg-black/[0.04]">{learner.industry}</span>}
          {(learner?.constraints?.geo || []).map((g) => (
            <span key={g} className="px-2.5 py-1 rounded-full bg-orange-500/10 text-orange-700">Target market · {g}</span>
          ))}
        </div>
      </div>
      <div className="flex items-center gap-1.5 text-orange-400 shrink-0">
        <CheckCircle2 size={16} />
        <span className="mono-label text-[10px]">Identified</span>
      </div>
    </div>
  </CardShell>
);

/* ------------------------------------------------------- Capability profile */
const NAME = {}; // filled lazily not needed

export const ProfileCard = ({ capabilities, building }) => (
  <CardShell testid="capability-profile" eyebrow="Your Capability Profile" icon={<Layers size={13} />} delay={0.05}>
    {building && (
      <div className="mb-4 flex items-center gap-2 text-sm text-slate-700">
        <span className="flex gap-1">
          <span className="dot w-1.5 h-1.5 rounded-full bg-orange-400" />
          <span className="dot w-1.5 h-1.5 rounded-full bg-orange-400" />
          <span className="dot w-1.5 h-1.5 rounded-full bg-orange-400" />
        </span>
        Building your learner profile…
      </div>
    )}
    <div className="space-y-4">
      {capabilities.map((c, i) => (
        <motion.div
          key={c.id}
          data-testid={`capability-row-${c.id}`}
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: i * 0.05 }}
        >
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-sm font-medium text-slate-900">{c.name}</span>
            <StatusBadge status={c.status} testid={`capability-badge-${c.status}`} />
          </div>
          <ScoreBar score={c.score} target={c.target} />
          {c.source && <div className="mt-1.5 text-[11px] text-slate-700">{c.source}</div>}
        </motion.div>
      ))}
    </div>
  </CardShell>
);

/* -------------------------------------------------------- Assessment evidence */
export const AssessmentCard = ({ assessment, evidenceCaps }) => (
  <CardShell testid="assessment-card" eyebrow="Adaptive Assessment · Live Evidence" icon={<Flame size={13} />}>
    <div className="mb-5 flex items-center gap-3">
      <div className="flex-1 h-1.5 rounded-full bg-black/[0.05] overflow-hidden">
        <motion.div className="h-full bg-orange-500 rounded-full"
          animate={{ width: `${(assessment.index / assessment.total) * 100}%` }} transition={{ duration: 0.5 }} />
      </div>
      <span className="font-mono text-xs text-slate-700">{Math.min(assessment.index, assessment.total)}/{assessment.total}</span>
    </div>
    <div className="space-y-3">
      <AnimatePresence>
        {evidenceCaps.map((c) => (
          <motion.div
            key={c.id}
            layout
            initial={{ opacity: 0, y: 10, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            className="rounded-xl p-4 bg-black/[0.03] border border-black/[0.08]"
            data-testid={`evidence-${c.id}`}
          >
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-slate-900">{c.name}</span>
              <div className="flex items-center gap-3">
                {c.status === "verified" && (
                  <span className="mono-label text-[9px] text-orange-400 flex items-center gap-1">
                    <CheckCircle2 size={11} /> Evidence added
                  </span>
                )}
                <span className="font-mono text-lg font-bold" style={{ color: scoreColor(c.score) }}>
                  {c.score == null ? "—" : <CountUp value={c.score} />}
                </span>
              </div>
            </div>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  </CardShell>
);

/* ------------------------------------------------------------- Capability gap */
const GapColumn = ({ title, color, items, testid }) => (
  <div data-testid={testid}>
    <div className="mono-label mb-3 flex items-center gap-2" style={{ color }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: color }} />{title}
    </div>
    <div className="space-y-2">
      {items.length === 0 && <div className="text-xs text-slate-700">—</div>}
      {items.map((c) => (
        <div key={c.id} className="flex items-center justify-between rounded-lg px-3 py-2 bg-black/[0.03] border border-black/[0.08]">
          <span className="text-[13px] text-slate-800">{c.name}</span>
          <span className="font-mono text-sm font-semibold" style={{ color }}>
            {c.score == null ? "?" : c.score}
          </span>
        </div>
      ))}
    </div>
  </div>
);

export const GapCard = ({ gap }) => (
  <CardShell testid="capability-gap" eyebrow="Your Capability Gap" icon={<TrendingUp size={13} />}>
    <div className="mb-6 flex items-center gap-3 text-sm">
      <span className="text-slate-700">Target outcome</span>
      <ArrowRight size={14} className="text-orange-400" />
      <span className="font-medium text-slate-900">{gap.target_outcome}</span>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <GapColumn title="Already Strong" color="#10B981" items={gap.strong} testid="gap-strong" />
      <GapColumn title="Developing" color="#F59E0B" items={gap.developing} testid="gap-developing" />
      <GapColumn title="Critical Gaps" color="#EF4444" items={gap.critical} testid="gap-critical" />
    </div>
    <div className="mt-7 flex items-center justify-center gap-3 sm:gap-5 text-center">
      <div className="flex-1">
        <div className="mono-label text-slate-700 text-[9px]">Where you are</div>
      </div>
      <ArrowRight size={16} className="text-orange-400" />
      <div className="flex-1">
        <div className="mono-label text-red-400/80 text-[9px]">Capability gap</div>
      </div>
      <ArrowRight size={16} className="text-orange-400" />
      <div className="flex-1">
        <div className="mono-label text-orange-400 text-[9px]">Where you're going</div>
      </div>
    </div>
  </CardShell>
);

/* ------------------------------------------------------ Processing sequence */
export const ProcessingCard = ({ steps, title = "Building your pathway" }) => {
  const [done, setDone] = useState(0);
  useEffect(() => {
    setDone(0);
    const per = Math.max(160, Math.min(300, 2400 / steps.length));
    const timers = steps.map((_, i) => setTimeout(() => setDone(i + 1), per * (i + 1)));
    return () => timers.forEach(clearTimeout);
  }, [steps]);
  return (
    <CardShell testid="processing-card" eyebrow="Mentis is working" icon={<MentisMark size={14} />}>
      <div className="space-y-2.5">
        {steps.map((s, i) => (
          <motion.div key={s} className="flex items-center gap-3 text-sm"
            animate={{ opacity: i < done ? 1 : 0.35 }}>
            {i < done ? <CheckCircle2 size={15} className="text-orange-400" />
              : <Circle size={15} className="text-slate-700" />}
            <span className={i < done ? "text-slate-900" : "text-slate-700"}>{s}{i === steps.length - 1 ? "…" : ""}</span>
          </motion.div>
        ))}
      </div>
    </CardShell>
  );
};

/* ------------------------------------------------------------- Pathway health */
const Metric = ({ label, value, testid, tone = "white" }) => (
  <div data-testid={testid} className="rounded-xl bg-black/[0.03] border border-black/[0.08] px-4 py-3">
    <div className="mono-label text-slate-700 text-[9px] mb-1.5">{label}</div>
    <div className={`font-display font-extrabold text-lg ${tone === "orange" ? "text-orange-400" : "text-slate-900"}`}>
      {value}
    </div>
  </div>
);

export const HealthBar = ({ health }) => (
  <motion.div layout data-testid="pathway-health" className="surface rounded-2xl p-5">
    <div className="mono-label text-orange-600 mb-4 flex items-center gap-2"><Gauge size={13} /> Pathway Health</div>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <Metric label="Goal Coverage" tone="orange" testid="metric-coverage" value={<><CountUp value={health.goal_coverage} />%</>} />
      <Metric label="Program Match" tone="orange" testid="metric-match" value={<><CountUp value={health.program_match} />%</>} />
      <Metric label="Gaps Covered" testid="metric-gaps" value={`${health.gaps_covered} / ${health.gaps_total}`} />
      <Metric label="Competencies" testid="metric-competencies" value={<CountUp value={health.competencies} />} />
      <Metric label="Duration" testid="metric-duration" value={health.duration} />
      <div className="rounded-xl bg-black/[0.03] border border-black/[0.08] px-4 py-3">
        <div className="mono-label text-slate-700 text-[9px] mb-1.5">Academic Req.</div>
        <div className="flex items-center gap-1.5 text-orange-400 font-semibold">
          {health.academic_requirements ? <CheckCircle2 size={16} /> : <X size={16} className="text-red-400" />}
          <span className="text-sm">{health.academic_requirements ? "Met" : "Unmet"}</span>
        </div>
      </div>
      <div className="rounded-xl bg-black/[0.03] border border-black/[0.08] px-4 py-3">
        <div className="mono-label text-slate-700 text-[9px] mb-1.5">Program Req.</div>
        <div className="flex items-center gap-1.5 text-orange-400 font-semibold">
          {health.program_requirements ? <CheckCircle2 size={16} /> : <X size={16} className="text-red-400" />}
          <span className="text-sm">{health.program_requirements ? "Met" : "Unmet"}</span>
        </div>
      </div>
    </div>
  </motion.div>
);

/* --------------------------------------------------------------- Roadmap */
const PHASE_ICON = { "01": Target, "02": BookOpen, "03": Layers, "04": ShieldCheck, "05": TrendingUp, "06": Award };

const CompetencyCard = ({ comp, onSelect }) => (
  <motion.button
    layout
    layoutId={`comp-${comp.id}`}
    onClick={() => onSelect(comp)}
    data-testid={`competency-card-${comp.id}`}
    initial={{ opacity: 0, scale: 0.96 }}
    animate={{ opacity: 1, scale: 1 }}
    exit={{ opacity: 0, scale: 0.9, x: -30 }}
    transition={{ type: "spring", stiffness: 320, damping: 30 }}
    whileHover={{ y: -3 }}
    className="text-left w-full rounded-xl p-4 bg-black/[0.03] border border-black/[0.08] hover:border-orange-500/40 transition-colors"
  >
    <div className="flex items-start justify-between gap-2">
      <span className="text-sm font-semibold text-slate-900 leading-snug">{comp.name}</span>
      {comp.required ? (
        <span className="mono-label text-[8px] text-amber-600 shrink-0">Required</span>
      ) : comp.cross_school ? (
        <span className="mono-label text-[8px] text-orange-400 shrink-0 flex items-center gap-1"><Network size={9} />Cross-school</span>
      ) : null}
    </div>
    <div className="mt-1.5 text-[11px] text-slate-700">{comp.school_short}</div>
    <div className="mt-3 flex flex-wrap gap-1.5">
      {comp.concepts.slice(0, 3).map((c) => (
        <span key={c} className="text-[10px] px-2 py-0.5 rounded-md bg-black/[0.03] text-slate-700">{c}</span>
      ))}
    </div>
    <div className="mt-3 flex items-center gap-2 text-[11px]">
      <span className="font-mono" style={{ color: scoreColor(comp.current) }}>{comp.current == null ? "?" : comp.current}</span>
      <ArrowRight size={11} className="text-slate-700" />
      <span className="font-mono text-orange-400">{comp.target}</span>
    </div>
  </motion.button>
);

export const PathwayRoadmap = ({ pathway, onSelect }) => (
  <CardShell testid="pathway-roadmap" eyebrow={pathway.title} icon={<MentisMark size={14} />}>
    <motion.div layout className="space-y-1">
      {pathway.phases.map((phase, pi) => {
        const Icon = PHASE_ICON[phase.code] || Layers;
        return (
          <div key={phase.code} data-testid={`pathway-phase-card-${phase.code}`}>
            {pi > 0 && <div className="phase-connector" />}
            <div className="flex items-center gap-3 mb-3">
              <div className="w-11 h-11 rounded-xl bg-orange-500/10 border border-orange-500/20 flex items-center justify-center">
                <Icon size={17} className="text-orange-400" />
              </div>
              <div>
                <div className="mono-label text-[9px] text-slate-700">Phase {phase.code}</div>
                <div className="font-display font-bold text-slate-900">{phase.name}</div>
              </div>
            </div>
            <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 gap-3 ml-1">
              <AnimatePresence mode="popLayout">
                {phase.competencies.map((c) => (
                  <CompetencyCard key={c.id} comp={c} onSelect={onSelect} />
                ))}
              </AnimatePresence>
            </motion.div>
          </div>
        );
      })}
    </motion.div>
  </CardShell>
);

/* ----------------------------------------------------- Competency detail modal */
export const CompetencyDetail = ({ comp, onClose }) => (
  <AnimatePresence>
    {comp && (
      <motion.div
        className="fixed inset-0 z-[60] flex items-center justify-center p-4"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        data-testid="competency-detail"
      >
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
        <motion.div
          initial={{ opacity: 0, y: 24, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 24, scale: 0.97 }}
          className="relative surface rounded-2xl p-7 max-w-lg w-full max-h-[85vh] overflow-y-auto scroll-thin"
        >
          <button onClick={onClose} data-testid="competency-detail-close"
            className="absolute top-5 right-5 text-slate-700 hover:text-slate-900 transition-colors"><X size={18} /></button>
          <div className="mono-label text-orange-600 mb-1">{comp.school_name}</div>
          <h3 className="font-display text-2xl font-extrabold text-slate-900 tracking-tight">{comp.name}</h3>
          <div className="mt-5 grid grid-cols-2 gap-3">
            <div className="rounded-xl bg-black/[0.03] border border-black/[0.08] p-4">
              <div className="mono-label text-slate-700 text-[9px] mb-1">Your current level</div>
              <div className="font-mono text-3xl font-bold" style={{ color: scoreColor(comp.current) }}>{comp.current ?? "?"}</div>
            </div>
            <div className="rounded-xl bg-black/[0.03] border border-black/[0.08] p-4">
              <div className="mono-label text-slate-700 text-[9px] mb-1">Target level</div>
              <div className="font-mono text-3xl font-bold text-orange-400">{comp.target}</div>
            </div>
          </div>
          <Section title="Why Mentis added this">{comp.why}</Section>
          <Section title="What you will master">
            <div className="flex flex-wrap gap-2 mt-1">
              {comp.concepts.map((c) => (
                <span key={c} className="text-xs px-2.5 py-1 rounded-md bg-orange-500/10 text-orange-700">{c}</span>
              ))}
            </div>
          </Section>
          {comp.status && (
            <Section title="How you will prove it">
              A scenario-based competency assessment with practitioner review builds verifiable evidence for this capability.
            </Section>
          )}
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

const Section = ({ title, children }) => (
  <div className="mt-5">
    <div className="mono-label text-slate-700 text-[9px] mb-2">{title}</div>
    <div className="text-sm text-slate-700 leading-relaxed">{children}</div>
  </div>
);

/* --------------------------------------------------------------- Matching */
export const MatchingCard = ({ steps }) => {
  const [done, setDone] = useState(0);
  useEffect(() => {
    setDone(0);
    const timers = steps.map((_, i) => setTimeout(() => setDone(i + 1), 400 * (i + 1)));
    return () => timers.forEach(clearTimeout);
  }, [steps]);
  return (
    <CardShell testid="matching-card" eyebrow="Checking your pathway" icon={<MentisMark size={14} />}>
      <div className="space-y-2.5">
        {steps.map((s, i) => (
          <div key={s} className="flex items-center gap-3 text-sm">
            {i < done ? <CheckCircle2 size={15} className="text-orange-400" /> : <Circle size={15} className="text-slate-700" />}
            <span className={i < done ? "text-slate-900" : "text-slate-700"}>{s}</span>
          </div>
        ))}
      </div>
    </CardShell>
  );
};

/* ---------------------------------------------------------- Program match */
export const ProgramMatchCard = ({ match }) => (
  <CardShell testid="program-match-card" eyebrow="Best Match" icon={<GraduationCap size={13} />}>
    <div className="flex items-start justify-between gap-4">
      <div>
        <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">{match.name}</h2>
        <div className="mt-1.5 text-sm text-slate-700">{match.school} · {match.duration}</div>
      </div>
      <div className="text-right shrink-0">
        <div className="font-display text-4xl font-extrabold text-orange-400"><CountUp value={match.match} />%</div>
        <div className="mono-label text-[9px] text-slate-700">Program Match</div>
      </div>
    </div>
    <p className="mt-4 text-sm text-slate-700 leading-relaxed">{match.why}</p>
    <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
      <div>
        <div className="mono-label text-orange-600 text-[9px] mb-2">Personalized for you</div>
        <div className="space-y-1.5">
          {match.personalized.length === 0 && <div className="text-xs text-slate-700">Standard pathway</div>}
          {match.personalized.map((c) => (
            <div key={c.id} className="text-[13px] text-slate-800 flex items-center gap-2"><Sparkles size={12} className="text-orange-400" />{c.name}</div>
          ))}
        </div>
      </div>
      <div>
        <div className="mono-label text-amber-600 text-[9px] mb-2">Required · cannot be changed</div>
        <div className="space-y-1.5">
          {match.locked.map((c) => (
            <div key={c.id} className="text-[13px] text-slate-800 flex items-center gap-2"><ShieldCheck size={12} className="text-amber-400" />{c.name}</div>
          ))}
        </div>
      </div>
    </div>
  </CardShell>
);

/* ------------------------------------------------------------- Application */
export const ApplicationCard = ({ application }) => (
  <CardShell testid="application-card" eyebrow="Your Application" icon={<CheckCircle2 size={13} />}>
    <div className="mb-5">
      <div className="flex items-center justify-between mb-2">
        <span className="font-display font-bold text-slate-900">
          {application.submitted ? "Application submitted" : "Application"}
        </span>
        <span className="font-mono text-sm text-orange-400 font-semibold"><CountUp value={application.percent} />% complete</span>
      </div>
      <div className="h-2 rounded-full bg-black/[0.05] overflow-hidden">
        <motion.div className="h-full bg-orange-500 rounded-full"
          animate={{ width: `${application.percent}%` }} transition={{ duration: 0.8 }} />
      </div>
    </div>
    <div className="space-y-2.5">
      {application.steps.map((s) => (
        <div key={s.id} data-testid={`app-step-${s.id}`}
          className="flex items-center gap-3 rounded-lg px-3.5 py-2.5 bg-black/[0.03] border border-black/[0.08]">
          {s.done ? <CheckCircle2 size={16} className="text-orange-400" /> : <Circle size={16} className="text-slate-700" />}
          <span className={`text-sm ${s.done ? "text-slate-900" : "text-slate-700"}`}>{s.label}</span>
        </div>
      ))}
    </div>
    {application.submitted && (
      <div className="mt-5 rounded-xl p-4 bg-orange-500/10 border border-orange-500/20 text-sm text-orange-700">
        <div className="flex items-center gap-2 font-semibold text-orange-700 mb-1"><CheckCircle2 size={15} /> Application received</div>
        Your learner profile has been saved and will continue with you into Mentogram.
      </div>
    )}
  </CardShell>
);

/* ----------------------------------------------------------- Goal strip */
export const GoalStrip = ({ goal, learner }) => (
  <motion.div layout data-testid="goal-strip"
    initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
    className="surface rounded-2xl px-5 py-4 flex items-center justify-between gap-4 flex-wrap">
    <div className="flex items-center gap-3">
      <Target size={16} className="text-orange-400" />
      <div>
        <div className="mono-label text-slate-700 text-[9px]">Your Goal</div>
        <div className="font-display font-bold text-slate-900 text-[15px]">{goal?.title}</div>
      </div>
    </div>
    <div className="flex flex-wrap items-center gap-2">
      {(learner?.constraints?.geo || []).map((g) => (
        <span key={g} data-testid={`goal-constraint-geo-${g}`}
          className="px-2.5 py-1 rounded-full bg-orange-500/10 text-orange-700 text-xs">Target market · {g}</span>
      ))}
      {(learner?.constraints?.focus || []).map((f) => (
        <span key={f} className="px-2.5 py-1 rounded-full bg-orange-500/10 text-orange-700 text-xs capitalize">{f} focus</span>
      ))}
      {learner?.constraints?.model && (
        <span className="px-2.5 py-1 rounded-full bg-black/[0.04] text-slate-700 text-xs capitalize">{learner.constraints.model} manufacturing</span>
      )}
    </div>
  </motion.div>
);

/* --------------------------------------------------------------- Idle hero */
export const WorkspaceIdle = () => (
  <div className="h-full flex flex-col items-center justify-center text-center px-8" data-testid="workspace-idle">
    <MentisMark size={54} pulse />
    <div className="mono-label text-orange-600 mt-6">Mentis · Live Workspace</div>
    <p className="mt-3 max-w-sm text-slate-700 text-sm leading-relaxed">
      Tell Mentis your goal and this space will build your capability profile, assessment evidence and
      personalised pathway — live, as you talk.
    </p>
  </div>
);
